/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { TIME_UNITS } from 'constants';

import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import moment from 'moment';
import { defineMessages } from 'react-intl';

import { parseTime, validatePositive } from 'formFunctions';

const messages = defineMessages({
  lessThanFiveMinutes: 'Cannot be less than 5 minutes.',
});

/**
 *
 * @param {Object} input object with the currently set input values (for an KPI being edited)
 * @param {Array} inputMeta array with defaultValues for the input
 * @param {Object} scope object with scope filter data
 * returns an object with defaultValues for each input, but the current value always wins
 */
export function initializeInputs(input, inputMeta, scope) {
  const defaultInputValues = inputMeta.reduce((acc, eachInput) => {
    acc[eachInput.name] = eachInput.defaultValue;
    return acc;
  }, {});

  return merge(defaultInputValues, input, { scope });
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function initializeSchedule({ starting_at, every }) {
  if (starting_at && every) {
    const startTime = moment.utc(starting_at, 'HH:mm:ss').local();
    return {
      startTime: startTime.format('HH:mm'),
      units: every.match(/[H?T?D?M?]$/)[0],
      interval: parseInt(every.match(/\d+/), 10),
    };
  }
  return {
    startTime: null,
    units: null,
    interval: null,
  };
}

export function parseSchedule({ startTime, interval, units }) {
  if (!startTime || !interval || !units) {
    return {};
  }
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const starting_at = parseTime(startTime).format('HH:mm:ss');
  const every = `${interval}${units}`;
  return { starting_at, every };
}

export function validateSchedule(values, props) {
  const {
    intl: { formatMessage },
  } = props;
  const errors = { interval: validatePositive(values.interval, props) };

  if (!errors.interval) {
    errors.interval =
      values.units === TIME_UNITS.MINUTES && values.interval < 5
        ? formatMessage(messages.lessThanFiveMinutes)
        : null;
  }

  return Object.values(errors).find(error => !isNil(error)) ? errors : {};
}

export function initializeBacktrack({ hours, minutes, days }) {
  const range = hours || minutes || days;
  let rangeUnits;
  if (range) {
    if (hours) {
      rangeUnits = TIME_UNITS.HOURS;
    } else if (days) {
      rangeUnits = TIME_UNITS.DAYS;
    } else if (minutes) {
      rangeUnits = TIME_UNITS.MINUTES;
    }

    return range
      ? {
          range,
          rangeUnits,
        }
      : { range: null, rangeUnits: null };
  } // return empty if no backtrack set
  return {};
}

export function parseBacktrack({ range, rangeUnits, startTime }) {
  let hours = 0;
  let minutes = 0;
  let days = 0;

  if (!startTime) {
    return {};
  }

  if (range) {
    switch (rangeUnits) {
      case TIME_UNITS.DAYS:
        days = range;
        break;
      case TIME_UNITS.HOURS:
        hours = range;
        break;
      case TIME_UNITS.MINUTES:
      default:
        minutes = range;
        break;
    }
  }
  return {
    ...(hours && hours !== 0 ? { hours } : {}),
    ...(minutes && minutes !== 0 ? { minutes } : {}),
    ...(days && days !== 0 ? { days } : {}),
  };
}

export function validateBacktrack(values, props) {
  const errors = { range: validatePositive(values.range, props) };

  return Object.values(errors).find(error => !isNil(error)) ? errors : {};
}

export function convertToMinutes(value, units) {
  switch (units) {
    case TIME_UNITS.HOURS:
      return value * 60;
    case TIME_UNITS.DAYS:
      return value * 60 * 24;
    default:
      return value;
  }
}

export function isScheduleMoreThanBacktrack(values) {
  const { range, rangeUnits, interval, units } = values;

  const rangeInMinutes = convertToMinutes(range, rangeUnits);
  const intervalInMinutes = convertToMinutes(interval, units);

  return rangeInMinutes < intervalInMinutes;
}
